import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Search, Filter, MoreHorizontal, Plus, Download, Edit, Trash2, Bell, Mail, MessageSquare, Phone } from 'lucide-react';

interface Notificacao {
  id: string;
  tipo: 'email' | 'sms' | 'whatsapp' | 'push';
  titulo: string;
  descricao: string;
  ativo: boolean;
  template: string;
  configuracao: {
    antesVencimento: number;
    aposVencimento: number;
    frequencia: string;
  };
  ultimaEnviada?: string;
  totalEnviadas: number;
}

const Notificacoes: React.FC = () => {
  const { userProfile } = useAuth();
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showBatchActions, setShowBatchActions] = useState(false);

  const loadNotificacoes = React.useCallback(async () => {
    if (!userProfile?.empresaCnpj) return;
    
    try {
      setLoading(true);
      // Simular dados de notificações (em produção, viria do Firebase)
      const notificacoesData: Notificacao[] = [
        {
          id: '1',
          tipo: 'email',
          titulo: 'Lembrete de Vencimento',
          descricao: 'Notificação enviada 3 dias antes do vencimento',
          ativo: true,
          template: 'Olá {nome}, sua mensalidade vence em {dias} dias. Valor: {valor}',
          configuracao: {
            antesVencimento: 3,
            aposVencimento: 1,
            frequencia: 'diaria'
          },
          ultimaEnviada: '2024-01-15T10:30:00Z',
          totalEnviadas: 45
        },
        {
          id: '2',
          tipo: 'whatsapp',
          titulo: 'Cobrança Vencida',
          descricao: 'Notificação enviada após vencimento',
          ativo: true,
          template: 'Olá {nome}, sua mensalidade está vencida. Valor: {valor}. Pague agora!',
          configuracao: {
            antesVencimento: 0,
            aposVencimento: 1,
            frequencia: 'diaria'
          },
          ultimaEnviada: '2024-01-14T14:20:00Z',
          totalEnviadas: 12
        },
        {
          id: '3',
          tipo: 'sms',
          titulo: 'Confirmação de Pagamento',
          descricao: 'Notificação enviada após confirmação de pagamento',
          ativo: false,
          template: 'Pagamento confirmado! Obrigado, {nome}.',
          configuracao: {
            antesVencimento: 0,
            aposVencimento: 0,
            frequencia: 'imediata'
          },
          ultimaEnviada: '2024-01-13T16:45:00Z',
          totalEnviadas: 8
        }
      ];

      setNotificacoes(notificacoesData);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setLoading(false);
    }
  }, [userProfile?.empresaCnpj]);

  useEffect(() => {
    loadNotificacoes();
  }, [loadNotificacoes]);

  const filteredNotificacoes = notificacoes.filter(notificacao =>
    notificacao.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    notificacao.descricao.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'sms':
        return <MessageSquare className="h-4 w-4" />;
      case 'whatsapp':
        return <Phone className="h-4 w-4" />;
      case 'push':
        return <Bell className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'email':
        return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20';
      case 'sms':
        return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
      case 'whatsapp':
        return 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
      case 'push':
        return 'text-purple-600 bg-purple-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getTipoText = (tipo: string) => {
    switch (tipo) {
      case 'email':
        return 'E-mail';
      case 'sms':
        return 'SMS';
      case 'whatsapp':
        return 'WhatsApp';
      case 'push':
        return 'Push';
      default:
        return tipo;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalNotificacoes = filteredNotificacoes.length;
  const ativasNotificacoes = filteredNotificacoes.filter(n => n.ativo).length;
  const totalEnviadas = filteredNotificacoes.reduce((sum, n) => sum + n.totalEnviadas, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <span className="text-gray-500">Cobranças</span>
              </li>
              <li>
                <span className="text-gray-400">/</span>
              </li>
              <li>
                <span className="text-gray-900 font-medium">Notificações</span>
              </li>
            </ol>
          </nav>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Notificações</h1>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-sm font-medium text-gray-500">Total de Notificações</div>
          <div className="text-2xl font-bold text-gray-900">{totalNotificacoes}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-sm font-medium text-gray-500">Notificações Ativas</div>
          <div className="text-2xl font-bold text-green-600">{ativasNotificacoes}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-sm font-medium text-gray-500">Total Enviadas</div>
          <div className="text-2xl font-bold text-blue-600">{totalEnviadas}</div>
        </div>
      </div>

      {/* Search and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Procurar por título ou descrição"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-50"
          >
            <Filter className="h-4 w-4" />
            <span>Filtros</span>
          </button>
        </div>

        <div className="flex items-center space-x-3">
          <div className="relative">
            <button
              onClick={() => setShowBatchActions(!showBatchActions)}
              className="flex items-center space-x-2 px-4 py-2 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-50"
            >
              <MoreHorizontal className="h-4 w-4" />
              <span>Ações em lote</span>
            </button>
            
            {showBatchActions && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                <div className="py-1">
                  <button className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <Download className="h-4 w-4" />
                    <span>Exportar dados</span>
                  </button>
                  <button className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <Edit className="h-4 w-4" />
                    <span>Ativar/Desativar</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <button 
            onClick={() => {
              // Implementar criação de notificação
              alert('Funcionalidade de criação de notificação será implementada');
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            <span>Adicionar notificação</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de notificação
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">E-mail</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">SMS</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">WhatsApp</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">Push</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">Ativa</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">Inativa</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Frequência
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">Imediata</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">Diária</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">Semanal</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">
              Limpar
            </button>
            <button className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
              Aplicar
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Título
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Configuração
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estatísticas
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredNotificacoes.map((notificacao) => (
                <tr key={notificacao.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-3 ${getTipoColor(notificacao.tipo)}`}>
                        {getTipoIcon(notificacao.tipo)}
                      </div>
                      <div className="text-sm font-medium text-gray-900">{getTipoText(notificacao.tipo)}</div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{notificacao.titulo}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">{notificacao.descricao}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {notificacao.configuracao.antesVencimento > 0 && (
                        <div>{notificacao.configuracao.antesVencimento} dias antes</div>
                      )}
                      {notificacao.configuracao.aposVencimento > 0 && (
                        <div>{notificacao.configuracao.aposVencimento} dias após</div>
                      )}
                      <div className="text-xs text-gray-500">{notificacao.configuracao.frequencia}</div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      notificacao.ativo 
                        ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20' 
                        : 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700'
                    }`}>
                      {notificacao.ativo ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div>{notificacao.totalEnviadas} enviadas</div>
                      {notificacao.ultimaEnviada && (
                        <div className="text-xs text-gray-500">
                          Última: {formatDate(notificacao.ultimaEnviada)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button className="text-primary-600 hover:text-primary-900">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button className="text-primary-600 hover:text-primary-900">
                        <Bell className="h-4 w-4" />
                      </button>
                      <button className="text-red-600 hover:text-red-900">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredNotificacoes.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">Nenhuma notificação encontrada</div>
          <div className="text-gray-400 text-sm mt-2">
            {searchTerm ? 'Tente ajustar os filtros de busca' : 'Comece criando sua primeira notificação'}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notificacoes;
